import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Dictionary, find, forEach, keyBy, map, transform, values} from 'lodash';

import {PEER_LINKS_TYPES} from '../const';
import {useRackEditorStore} from './useRackEditorStore';

import Node from '../store/Node';
import LinksGroup from '../store/LinksGroup';
import RackStore from '../store/RackStore';
import Step from '../../cablingMapEditor/store/Step';
import {PeerLinksTypesType} from '../types';
import {Change} from '../../cablingMapEditor/store/ChangesStore';

// Attempting to reuse existing links groups for the same peering type or create new ones
const getPeerLink = (node: Node, peeringType: PeerLinksTypesType, rackStore: RackStore): LinksGroup => {
  const existingLink = find(node.myLinksGroups, {peeringType}) as LinksGroup;
  return existingLink ||
    LinksGroup.createFilledWith(
      {fromName: node.name, toName: node.name, count: 0, peeringType},
      rackStore
    );
};

const usePeerLinker = () => {
  const {rackStore, sourceNode} = useRackEditorStore();
  const {changes} = rackStore;

  const reusedChange = useRef(false);
  const hasChanges = useRef(false);

  // Depending on the node role and peering type there could be
  // different combinations of peer links
  const peeringTypes = sourceNode!.isLeaf ?
    (
      // MLAG Leaf pairs have L2* and L3 peer links
      // ESI Leaf pairs do not have peer links
      sourceNode!.isMlagLeafPair ? [PEER_LINKS_TYPES.L2_PEER, PEER_LINKS_TYPES.L3_PEER] : []
    ) : (
      // Access Switch pairs must have L3* peer links
      sourceNode!.isAccessSwitch ? [PEER_LINKS_TYPES.L3_PEER] : []
    );

  // Reusing/creating links groups for every required peering type.
  // New links groups get created of zero size.
  const [linksGroups] = useState<Dictionary<LinksGroup>>(() => {
    return transform(
      peeringTypes,
      (acc, type) => {
        acc[type] = getPeerLink(sourceNode!, type, rackStore);
      },
      {}
    );
  });

  // Identify/create the change.
  // Change must contain:
  // * LinksGroups for all peering types
  // * Node for portChannelId[Ext]
  const [change] = useState<Change | null>(() => {
    const links = values(linksGroups);
    if (changes.currentEqualsTo([...links, sourceNode!], true)) {
      reusedChange.current = true;
      return changes.current;
    } else {
      return map([...links, sourceNode], (item) => Step.modification(item!, item!));
    }
  });

  // Registering all affected peer links in the links groups collection
  useEffect(
    () => {
      forEach(values(linksGroups), (link) => rackStore.addLinksGroup(link));
      return () => {
        // If no changes were made but the new change has been registered, roll it back
        if (!reusedChange.current && !hasChanges.current) {
          rackStore.changes.rollBack();
        }
      };
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Make all steps involved accessible by item id
  const stepsById = useMemo(
    () => keyBy(change, 'id'),
    [change]
  );

  const trackChange = useCallback(
    ((parameter, value: unknown, item: Node | LinksGroup) => {
      // Apply changes
      item.setProperty(parameter, value, true);
      // Update corresponding step
      stepsById[item.id].setResult(item);
      // Register changes, making sure no duplicates get created
      rackStore.changes.register(change!, false, true);
      hasChanges.current = true;
    }),
    [change, rackStore.changes, stepsById]
  );

  const {portChannelIdsErrors} = sourceNode!;

  return {node: sourceNode, portChannelIdsErrors, linksGroups, trackChange};
};

export default usePeerLinker;
